import axios from 'axios';

// Base URL configuration
const API_URL = '/api';

// Create API instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Token management functions
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  
  // Validate JWT format (3 parts separated by dots)
  if (token && token.includes('.') && token.split('.').length === 3) {
    return token;
  }
  
  return null;
};

const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('isAuthenticated');
  console.log('Tokens cleared');
};

// Request interceptor to add authorization header
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log(`Auth header added for ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh and subscription errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle subscription errors (403 with specific codes)
    if (error.response?.status === 403) {
      const errorCode = error.response.data?.code;
      
      if (errorCode === 'SUBSCRIPTION_REQUIRED' || errorCode === 'PLAN_UPGRADE_REQUIRED') {
        console.log('Subscription required, redirecting to subscribe page');
        // Optionally redirect to subscribe page
        // window.location.href = '/subscribe';
        
        // Or let the component handle it by passing through the error
        return Promise.reject(error);
      }
    }

    // If 401 and not already retried, try token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('401 received, attempting token refresh...');
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          console.log('No refresh token available');
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });

        const { access_token, refresh_token } = response.data;

        localStorage.setItem('token', access_token);
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }

        console.log('Token refreshed successfully');
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return axios(originalRequest);

      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        clearTokens();
        window.location.href = '/login?error=session_expired';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: async (credentials) => {
    console.log('Login attempt');
    const response = await api.post('/auth/login', credentials);

    const { access_token, refresh_token, user } = response.data;

    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
    }
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('isAuthenticated', 'true');

    console.log('Login successful');
    return response;
  },

  register: async (userData) => {
    console.log('Registration attempt');
    const response = await api.post('/auth/register', userData);

    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('isAuthenticated', 'true');
    }

    console.log('Registration successful');
    return response;
  },

  getProfile: () => api.get('/auth/profile'),
  
  // Get subscription status from backend
  getSubscription: () => api.get('/auth/subscription'),
  
  // Check if user has access to a specific feature
  checkFeatureAccess: (feature) => api.post('/auth/subscription/check', { feature }),

  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh', {}, {
      headers: { 'Authorization': `Bearer ${refreshToken}` }
    });

    const { access_token, refresh_token } = response.data;

    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
    }

    return response;
  },

  logout: () => {
    clearTokens();
    console.log('Logged out');
  }
};

// Subscription service
export const subscriptionService = {
  getStatus: () => api.get('/subscription/status'),
  
  checkout: (planType) => {
    let userEmail = '';
    try {
      const userInfo = localStorage.getItem('user');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        userEmail = user.email || '';
      }
    } catch (e) {
      console.error('Error getting user email:', e);
    }

    return api.post('/subscription/checkout', {
      plan: planType,
      email: userEmail
    });
  },

  cancelSubscription: () => api.post('/subscription/cancel')
};

// Export api as apiClient for compatibility
export const apiClient = api;

export default api;